const CHECKOUT_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";
const PLACEHOLDER_CONTACT_IN = "9000090000";
const PLACEHOLDER_CONTACT_US = "+12015550123";

function loadCheckoutScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Checkout is only available in the browser"));
  }
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${CHECKOUT_SCRIPT_URL}"]`);
    if (existing) {
      if (window.Razorpay) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load checkout")), {
        once: true,
      });
      return;
    }
    const script = document.createElement("script");
    script.src = CHECKOUT_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load checkout"));
    document.body.appendChild(script);
  });
}

function compactPrefill(obj) {
  return Object.fromEntries(
    Object.entries(obj || {}).filter(([, value]) => value != null && String(value).trim() !== "")
  );
}

function indianMobile(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length >= 12 && digits.startsWith("91")) return digits.slice(-10);
  if (digits.length === 11 && digits.startsWith("0")) return digits.slice(-10);
  if (digits.length === 10) return digits;
  return "";
}

export function prefillFromClerk(user) {
  if (!user) return {};
  const email =
    user.primaryEmailAddress?.emailAddress ||
    user.emailAddresses?.[0]?.emailAddress ||
    "";
  const phone =
    user.primaryPhoneNumber?.phoneNumber ||
    user.phoneNumbers?.[0]?.phoneNumber ||
    "";
  const name =
    [user.firstName, user.lastName].filter(Boolean).join(" ") || user.fullName || "";
  return compactPrefill({
    email,
    name,
    contact: indianMobile(phone),
  });
}

/**
 * Open hosted checkout for a backend payment session.
 * Resolves with generic { order_id, payment_id, subscription_id, signature }.
 */
export async function openHostedCheckout(session, clerkUser) {
  await loadCheckoutScript();
  if (!window.Razorpay) {
    throw new Error("Checkout is not available");
  }

  const checkout = session?.checkout || {};
  const currency = String(session.currency || "INR").toUpperCase();
  const prefill = compactPrefill({
    ...prefillFromClerk(clerkUser),
    ...(session.prefill || {}),
  });
  if (currency === "USD") {
    const digits = String(prefill.contact || "").replace(/\D/g, "");
    const looksUs = digits.startsWith("1") && digits.length >= 11;
    prefill.contact = looksUs ? `+${digits}` : PLACEHOLDER_CONTACT_US;
  } else {
    prefill.contact = indianMobile(prefill.contact) || PLACEHOLDER_CONTACT_IN;
  }

  const options = {
    key: session.public_key,
    amount: session.amount,
    currency,
    name: "LazyStudio",
    description: session.description || "Credits",
    prefill,
    // Email is optional so it can be hidden. Phone is mandatory on Indian
    // Checkout — hiding it makes Razorpay ask again. Prefill + readonly skips typing.
    hidden: {
      email: true,
    },
    readonly: {
      email: true,
      contact: true,
      name: true,
    },
    theme: { color: "#111827" },
  };
  if (currency === "USD") {
    options.method = {
      card: true,
      netbanking: false,
      wallet: false,
      upi: false,
      emi: false,
      paylater: false,
    };
  }
  if (checkout.order_id) options.order_id = checkout.order_id;
  if (checkout.subscription_id) options.subscription_id = checkout.subscription_id;

  return new Promise((resolve, reject) => {
    const widget = new window.Razorpay({
      ...options,
      handler(response) {
        resolve({
          order_id: response.razorpay_order_id,
          payment_id: response.razorpay_payment_id,
          subscription_id: response.razorpay_subscription_id,
          signature: response.razorpay_signature,
        });
      },
      modal: {
        ondismiss() {
          const err = new Error("Checkout closed");
          err.cancelled = true;
          reject(err);
        },
      },
    });
    widget.on("payment.failed", (response) => {
      const err = new Error(response?.error?.description || "Payment failed");
      err.failed = true;
      reject(err);
    });
    widget.open();
  });
}
